import { journalBoundaryContext } from '../ability/boundary-journal.js';
import { resolveEngineFrame, resolvedVerdict, type AbilityUseReaders, type AbilityUseVerdict } from '../actions/ability-use.js';
import { type JanusCtx, type Row, type StatusRow, ecId, HandBack, Refusal, scalar, upper } from '../ability/context.js';
import { executeTriggerDispatch, triggerExecutorGap } from '../ability/trigger-executor.js';
import { boundaryCheckpoint, releaseRollback } from '../ability/boundary-checkpoint.js';
import { janusCtxTriggerReaders } from '../reactions/janus-ctx-trigger-readers.js';
import { runTriggerDispatch, TRIGGER_CARRIERS, type TriggerCandidate } from '../reactions/trigger-dispatch.js';
import { skipUnported, unportedSkipRow } from '../unported.js';
import { AFTER_LONG_REST, longRestListeners, type LongRestListener } from './long-rest.js';
import type { RollSource } from '../actions/weapon-attack.js';
import { statusRemovalBefore } from '../statuses/expiry.js';
import { statusClockRow } from '../port/native-clock.js';

export const longRestTriggerSkip = (names: string[]): string => `long-rest:trigger-dispatch-unported (${[...new Set(names)].join(', ')})`;
export type LongRestTriggerVerdict = Extract<AbilityUseVerdict, { kind: 'resolved' }>;
export function longRestTriggerSkipFrame(names: string[], detail?: Record<string, unknown>): LongRestTriggerVerdict {
  return { kind: 'resolved', rows: names.length ? [skipUnported(longRestTriggerSkip(names), detail)] : [],
    ec: {}, statusesAdded: [], statusesRemoved: [], charges: [], playerCharactersChanged: [], inventory: [], deathTail: [], concentrationBreaks: [], statusStacks: [] };
}

/** The ability lane saves absolute added stacks and increments. A decrement uses its existing exact-row replacement shape. */
function saveableStacks(verdict: LongRestTriggerVerdict, members: ReadonlyMap<string, Row>, read: AbilityUseReaders): LongRestTriggerVerdict {
  const frame = { ...verdict, statusesRemoved: [...verdict.statusesRemoved], statusesAdded: [...verdict.statusesAdded], statusStacks: [] as LongRestTriggerVerdict['statusStacks'] };
  for (const change of verdict.statusStacks) {
    if (change.status_stack >= change.before) { frame.statusStacks.push(change); continue; }
    const holder = members.get(change.target);
    const status = holder && read.statuses(holder).find(s => upper(s.status_id) === change.status_id);
    if (!status) throw new HandBack('long-rest:stack-row-not-held');
    const before = statusRemovalBefore(status, change.target), clock = statusClockRow(status, scalar);
    const added: Row = { status_id: read.uuid(), status_item_id: change.status_item_id, target: change.target,
      caster: before.status_caster_id, status_connected_id: before.status_connected_id || read.uuid(), status_stack: change.status_stack,
      ends: ['year', 'month', 'day', 'hour', 'minute', 'second'].map(k => clock['status_ends_campaign_' + k]).join('-') };
    for (const key of ['status_ends_on_target_start_of_turn', 'status_ends_on_target_end_of_turn', 'status_ends_on_caster_start_of_next_turn', 'status_ends_on_caster_end_of_next_turn',
      'status_aoe_source', 'status_opposing_team_only', 'status_aoe_distance', 'status_aoe_distance_depth', 'status_aoe_distance_height',
      'status_origin_point_x', 'status_origin_point_y', 'status_origin_point_z', 'status_distance_point_x', 'status_distance_point_y', 'status_distance_point_z'])
      added[key] = Number(scalar(read.value(status, key)) ?? 0);
    for (const key of ['status_aoe_type', 'status_origin_id', 'status_casting_targets_arr', 'status_damage_type']) added[key] = scalar(read.value(status, key)) || null;
    added.status_forced_roll_extreme = Number(scalar(read.value(status, 'status_forced_roll_extreme')) ?? 0);
    frame.statusesRemoved.push({ status_id: change.status_id, status_item_id: change.status_item_id, target: change.target });
    frame.statusesAdded.push(added as unknown as StatusRow);
  }
  return frame;
}

/** The entire rest boundary has landed. One actor's carriers share the ordinary trigger executor and one ability-truth frame. */
export function resolveLongRestTriggers(input: { actorId: string; rollPrivate?: number }, encounter: Row, members: ReadonlyMap<string, Row>,
  read: AbilityUseReaders, roll: RollSource): LongRestTriggerVerdict {
  const event = { trigger: AFTER_LONG_REST };
  const run = (ctx: JanusCtx): void => {
    ctx = journalBoundaryContext(ctx);
    const actor = ctx.members.get(input.actorId.toUpperCase());
    if (!actor) throw new HandBack('long-rest:actor-not-held');
    const listeners = longRestListeners(actor, ctx.read);
    const names = new Set<string>();
    const failures: Record<string, string> = {};
    const skip = (listener: LongRestListener, reason = 'definition-not-held'): void => { names.add(listener.name); failures[listener.name] = reason; };
    for (const listener of listeners) if (!listener.definition) skip(listener);
    const base = janusCtxTriggerReaders(ctx);
    // The resolver owns listener identity and inactive-copy precedence. Keep unrelated unheld carriers out of this event's collector.
    const dispatchRead = { ...base,
      entries: (row: Row | null, key: string): unknown[] => ecId(row) === ecId(actor) && key !== AFTER_LONG_REST
        && (TRIGGER_CARRIERS as readonly string[]).includes(key)
        ? listeners.filter(l => l.carrier === key && l.definition).map(l => ({ ...l.entry, ability_id: l.id, item_arr: l.definition }))
        : base.entries(row, key),
      statuses: (row: Row): Row[] => ecId(row) === ecId(actor)
        ? listeners.filter(l => l.statusId && l.definition).map(l => ({ ...l.entry, status_arr: l.definition })) : base.statuses!(row),
    };
    // carrierEntries also unions raw root buckets. Its input must carry the same normalized inventory.
    const owner = { ...actor };
    for (const key of TRIGGER_CARRIERS)
      owner[key] = dispatchRead.entries(actor, key);
    const listenerFor = (candidate: TriggerCandidate): LongRestListener => listeners.find(l => l.id === candidate.abilityId
      && (candidate.pool === 'carrier' ? l.carrier === candidate.carrier : l.statusId === candidate.statusId))!;
    runTriggerDispatch(dispatchRead, owner, event, null, (spec, _owner) => {
      const gap = triggerExecutorGap(ctx, spec.candidate, event);
      if (gap) throw new HandBack(gap);
      // Null inciting row and [actor] are complete_long_rest's janus_trigger call, including Campfire Beat's frame target.
      ctx.statusConnectedId = null;
      executeTriggerDispatch(ctx, event, spec, actor, [actor]);
      return owner;
    }, (candidate, attempt) => {
      const restore = boundaryCheckpoint(ctx);
      try { attempt(); return true; }
      catch (error) {
        restore();
        if (!(error instanceof HandBack || error instanceof Refusal)) throw error;
        skip(listenerFor(candidate), error instanceof HandBack ? error.reason : error.message);
        return false;
      } finally { releaseRollback(restore); }
    });
    if (names.size) ctx.rows.push(ctx.dryRun ? unportedSkipRow(longRestTriggerSkip([...names])) : skipUnported(longRestTriggerSkip([...names]), { actorId: ecId(actor), failures }));
    const frame = saveableStacks(resolvedVerdict(ctx), members, ctx.read);
    ctx.statusesAdded = frame.statusesAdded; ctx.statusesRemoved = frame.statusesRemoved; ctx.statusStacks = frame.statusStacks;
  };
  const result = resolveEngineFrame(input, encounter, members, read, roll, () => {}, run);
  if (result.kind === 'resolved') return resolvedVerdict(result.ctx);
  // A frame dependency failed before a supported save could be decided. The boundary still completed and names every owed listener.
  const actor = members.get(input.actorId.toUpperCase());
  const listeners = actor ? longRestListeners(actor, read) : [];
  return longRestTriggerSkipFrame(listeners.map(l => l.name), { reason: result.reason });
}
